import { useEffect, useState } from 'react';
import { MailWarning, Loader2, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';

/**
 * VerifyBanner — sticky strip at the top of every page for unverified
 * users. Lets them resend the verification email. Disappears the moment
 * the user becomes verified (polled lightly on mount + after resend).
 *
 * Consumers: rendered once inside ProtectedRoute so it shows across every
 * authenticated screen.
 */
export default function VerifyBanner({ user }) {
  // Optimistic: hide instantly when user.email_verified is true. For older
  // users that DON'T have the field, the backfill on startup ensures the
  // field exists, so we default to true to avoid a spurious banner.
  const verified = user?.email_verified !== false;
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState(0); // seconds — soft client throttle
  const [verifiedNow, setVerifiedNow] = useState(verified);

  // 60s cooldown tick — prevents rapid re-taps from spamming the backend.
  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const t = setTimeout(() => setCooldownLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(t);
  }, [cooldownLeft]);

  // Light polling — every 20s — so if the user verifies in another tab the
  // banner self-dismisses.
  useEffect(() => {
    if (verifiedNow) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (data?.user?.email_verified) {
          setVerifiedNow(true);
        }
      } catch {}
    }, 20000);
    return () => clearInterval(t);
  }, [verifiedNow]);

  const resend = async () => {
    if (sending || cooldownLeft > 0) return;
    setSending(true);
    try {
      await api.post('/auth/resend-verification');
      setSent(true);
      setCooldownLeft(60);
      // Keep the hint visible long enough for users to actually go check
      // their inbox (and spam/promotions tab — the #1 reason "verification
      // email never arrived" tickets come in on Gmail).
      setTimeout(() => setSent(false), 15000);
    } catch {}
    setSending(false);
  };

  if (verifiedNow) return null;

  return (
    <div
      data-testid="verify-banner"
      className="fixed top-0 left-0 right-0 max-w-[480px] mx-auto z-[9998] bg-[#FFF7D6] border-b border-[#F5D77B] pt-[max(8px,env(safe-area-inset-top))]"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <MailWarning size={16} className="text-[#7A4F00] flex-shrink-0" strokeWidth={2.4} />
        <p className="text-[11px] font-bold text-[#7A4F00] leading-tight flex-1 min-w-0">
          {sent
            ? 'Sent! Check your inbox — also peek at Spam & Promotions.'
            : 'Verify your email to host plans, post & message.'}
        </p>
        {sent ? (
          <span data-testid="verify-banner-sent" className="flex items-center gap-1 text-[11px] font-bold text-[#3F5200] flex-shrink-0">
            <CheckCircle2 size={12} /> Sent
          </span>
        ) : (
          <button
            data-testid="verify-banner-resend"
            onClick={resend}
            disabled={sending || cooldownLeft > 0}
            className="text-[11px] font-extrabold text-[#7A4F00] bg-white px-2.5 py-1 rounded-full border border-[#F5D77B] disabled:opacity-60 active:scale-95 flex-shrink-0"
          >
            {sending ? <Loader2 size={11} className="animate-spin" /> : cooldownLeft > 0 ? `${cooldownLeft}s` : 'Resend'}
          </button>
        )}
      </div>
      {sent && (
        <div
          data-testid="verify-banner-deliverability-hint"
          className="px-3 pb-2 pt-1 text-[10.5px] leading-snug text-[#7A4F00]/85"
        >
          From <span className="font-bold">onboarding@resend.dev</span>. Gmail may sort it into <span className="font-bold">Promotions</span> or <span className="font-bold">Spam</span> — mark it as Not Spam so future ASAP mail lands in your inbox.
        </div>
      )}
    </div>
  );
}
